/**
 * OSMODI - side-cart quick add pre-empt (+ small drawer polish)
 *
 * The recommendation rail in the drawer wires [data-oc-quickadd] buttons
 * through THREE different scripts:
 *
 *   osmodi-commerce.js              per-button click (bubble)
 *   osmodi-commerce-polish.js       per-button aria-busy click (capture)
 *   osmodi-product-reference-v2.js  document click (capture)
 *
 * The third one asks Shopify for the entire cart-drawer section along with
 * the add, then re-renders the drawer's markup from that payload. That is
 * what makes the drawer visibly "reload" after a click - the recommendation
 * rail loses its DOM identity and scroll position while every sibling block
 * flashes.
 *
 * Fix: register a document capture-phase click listener FIRST, so this file
 * has to load without `defer` at the very top of <head> (see layout/theme).
 * On any [data-oc-quickadd] click inside the drawer we:
 *   1. stopImmediatePropagation - the other capture handler never fires
 *   2. add via /cart/add.js (no sections request, no full re-render)
 *   3. surgically patch only the blocks whose markup actually changed
 *   4. update the header count badge directly, in case a listener misses it
 *
 * Also hides the recommendation-card size pill when the variant title is the
 * literal string "Default Title" - CSS can't match text content, so we do
 * this in JS both at init and via an observer for late-arriving markup.
 *
 * The recommendation rail is deliberately never touched - it must keep its
 * node identity and horizontal scroll position through an add.
 */
(() => {
  const DRAWER = '.cart-drawer__inner';
  const SECTION = 'cart-drawer-section';

  /* Blocks that patchDrawer keeps in sync after an add.
     .osmodi-cart-recommendations is absent on purpose. */
  const SYNC = [
    '.theme-drawer__title',
    '.osmodi-cart-shipping',
    '.cart-items__table',
    '.osmodi-cart-promise',
    '.cart-drawer__summary',
  ];

  const CHECK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"/></svg>';

  /* -------------------------------------------------------------------- */

  /* Header cart-count badge - the theme's own listener targets the wrong
     selector on the current header, so update it here to be certain. */
  function paintBadge(count) {
    document.querySelectorAll('[data-v4-cart-count]').forEach((badge) => {
      badge.textContent = String(count || 0);
      badge.hidden = !count;
    });
  }

  /* Hide the "Default Title" size pill on recommendation cards for products
     that have no meaningful variant name. Called on init and whenever the
     recommendation rail markup changes. */
  function hideDefaultTitlePills(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.osmodi-cart-recommendation__image span')
      .forEach((pill) => {
        const text = (pill.textContent || '').trim();
        pill.hidden = text === 'Default Title' || text === '';
      });
  }

  async function patchDrawer() {
    const host = document.querySelector(DRAWER);
    if (!host) return;

    const response = await fetch(`/?sections=${SECTION}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const payload = await response.json();
    const markup = payload[SECTION];
    if (!markup) return;

    const next = new DOMParser().parseFromString(markup, 'text/html');

    SYNC.forEach((selector) => {
      const current = host.querySelector(selector);
      const fresh = next.querySelector(selector);
      if (!current || !fresh) return;
      if (current.innerHTML.trim() !== fresh.innerHTML.trim()) {
        current.innerHTML = fresh.innerHTML;
      }
    });
  }

  /* -------------------------------------------------------------------- */

  document.addEventListener(
    'click',
    async (event) => {
      const button =
        event.target instanceof Element
          ? event.target.closest('[data-oc-quickadd]')
          : null;

      /* Only take over inside the drawer; quick add elsewhere keeps its
         existing behaviour. */
      if (!button || !button.closest(DRAWER)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.disabled) return;

      const originalLabel = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const added = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!added.ok) throw new Error('Unable to add this fragrance.');

        await patchDrawer();

        const cart = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        paintBadge(cart.item_count);

        /* Other listeners (announcements, analytics) update from this. */
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart } })
        );

        button.innerHTML = CHECK_ICON;
        window.setTimeout(() => {
          button.innerHTML = originalLabel;
        }, 1400);
      } catch (error) {
        button.innerHTML = originalLabel;
        console.error('[osmodi] quick add failed:', error);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    },
    true /* capture - must be registered before the other handlers */
  );

  /* -------------------------------------------------------------------- */

  function bootDrawerPolish() {
    hideDefaultTitlePills(document);

    /* The drawer swaps its markup on cart updates. An observer scoped to
       the drawer host re-runs the polish whenever its subtree changes. */
    const observe = () => {
      const host = document.querySelector(DRAWER);
      if (!host) return false;
      new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'childList' && m.addedNodes.length) {
            hideDefaultTitlePills(host);
            break;
          }
        }
      }).observe(host, { childList: true, subtree: true });
      return true;
    };

    if (!observe()) {
      /* Drawer isn't in the DOM yet during head parse; try again on load. */
      document.addEventListener('DOMContentLoaded', observe, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootDrawerPolish, {
      once: true,
    });
  } else {
    bootDrawerPolish();
  }
})();
